import { DependencyList } from "react"
import { Keys, Options, useHotkeys } from "react-hotkeys-hook"

import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useIsTypingInEditor from "@hooks/useIsTypingInEditor"

/**
 * Application-level hotkey hook that wraps react-hotkeys-hook with common behavior.
 *
 * Automatically handles:
 * - Disables hotkeys when any dialog is open
 * - Disables hotkeys when typing in editors (contenteditable or role="textbox")
 * - Disables hotkeys in form fields (input, textarea, select) by default
 * - Disables hotkeys in contenteditable elements
 *
 * @param keys - The hotkey combination (e.g., "c", "Mod+K", "space")
 * @param callback - The function to call when the hotkey is triggered
 * @param options - Additional options to pass to useHotkeys
 * @param deps - Dependency array for the callback
 *
 * @example
 * // Basic usage
 * useAppHotkey("c", () => openMenu())
 *
 * @example
 * // With additional enabled conditions
 * useAppHotkey("c", () => openMenu(), { enabled: !isDisabled })
 *
 * @example
 * // Allow hotkey in form fields (override default)
 * useAppHotkey("Mod+K", () => openSearch(), { enableOnFormTags: true })
 */
export default function useAppHotkey(
    keys: Keys,
    callback: (event: KeyboardEvent) => void,
    options?: Omit<Options, "enabled"> & { enabled?: boolean },
    deps?: DependencyList,
): void {
    const { isAnyDialogOpen } = useIsAnyDialogOpen()
    const isTypingInEditor = useIsTypingInEditor()

    // Merge provided options with defaults
    const mergedOptions: Options = {
        // Default: disable in form tags (input, textarea, select)
        enableOnFormTags: false,
        // Disable in contenteditable elements (safety net)
        enableOnContentEditable: false,
        // Spread user-provided options (can override defaults above)
        ...options,
        // Calculate final enabled state: user's enabled AND our global checks
        enabled: (options?.enabled ?? true) && !isAnyDialogOpen && !isTypingInEditor,
    }

    // Build dependency array: user deps + our state deps
    const finalDeps = [...(deps ?? []), isAnyDialogOpen, isTypingInEditor]

    useHotkeys(keys, callback, mergedOptions, finalDeps)
}
